import axios from "axios";
import { message } from "antd";
import secureLocalStorage from "react-secure-storage";
import CryptoJS from "crypto-js";
const mainDomain = process.env.REACT_APP_BACKEND;

const APICaller = async (
  role,
  token,
  endpoint,
  method,
  payload,
  responseType = "json",
) => {
  try {
    // ADD HERE
    const expiry = secureLocalStorage.getItem("accessTokenExpiry");
    if (expiry && Date.now() >= Number(expiry)) {
      secureLocalStorage.clear();
      window.location.href = "/";
      return Promise.reject("Session expired");
    }

    const isFormData = payload instanceof FormData;
    const response = await axios({
      url: `${mainDomain}${endpoint}`,
      method: method || "GET",
      data: payload,
      headers: {
        Accept: "application/json, text/plain, */*",
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        //"Access-Control-Allow-Origin": "*",
        Authorization: `Bearer ${token}`,
        role: role,
      },
      timeout: endpoint === "/downloadAll" ? 240000 : 60000,
      responseType: responseType,
    });

    let data = response.data;

    if (data?.dataVT !== undefined) {
      data = decrypt(data?.dataVT, process.env.REACT_APP_SSO_ENCRYPTION_KEY);

      data = data && JSON.parse(data);
    } else {
      data = response.data;
    }

    return data;
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      error.response.data
        .text()
        .then((text) => {
          console.error("BACKEND ERROR BODY:", text);
        })
        .catch((err) => {
          console.error("Unable to read backend error:", err);
        });
    }
    handleErrorResponse(error, token, role);
    throw error;
  }
};

// Function to decrypt the data
const decrypt = (encryptedData, encryptionKey) => {
  try {
    // Decode the base64 encrypted data into a byte array
    const combined = CryptoJS.enc.Base64.parse(encryptedData);

    // Extract salt (first 16 bytes)
    const salt = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));

    // Extract IV (next 16 bytes)
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(4, 8));

    // The remaining part is the encrypted content
    const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(8));

    // Derive the secret key using PBKDF2 with the provided salt
    const secretKey = deriveKey(encryptionKey, salt);

    // Decrypt the data using AES with the derived key and IV
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      secretKey,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7, // Ensure proper padding
      },
    );

    // Return the decrypted data as a UTF-8 string
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

    // Check if decryption is successful
    if (!decryptedText) {
      throw new Error("Decrypted data is empty or invalid UTF-8.");
    }

    return decryptedText;
  } catch (error) {
    console.error("Decryption error:", error.message);
    return null;
  }
};

const deriveKey = (password, salt) => {
  return CryptoJS.PBKDF2(password, salt, {
    keySize: 256 / 32,
    iterations: 65536,
  });
};

// Centralized error handling function
const handleErrorResponse = (error, token, role) => {
  let errorMsg = "";

  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    errorMsg =
      "The report is taking too long to generate. Please select a smaller month range.";

    showError(errorMsg);
    return;
  }

  // Case: No server response
  if (!error.response) {
    if (token && role !== "") {
      errorMsg = "Unable to connect to the server. Please sign-in later.";
    } else {
      errorMsg =
        "The server is currently offline. We’re performing maintenance at the moment. Sorry for the inconvenience.";
    }
    if (Array.isArray(errorMsg)) {
      // If it's an array (multiple validation errors), join with a new line for readability
      showError(errorMsg.join("\n"), "yes"); // Call a function to show the error in Ant Design's message component
    } else {
      // If it's a string, display as-is
      showError(errorMsg);
    }
  } else {
    // Case: Server responded with error
    let statusCode, errorMessage;

    // Only attempt to parse if response.data is a string
    try {
      // Handle case when error.response.data is an object (as expected)
      if (error.response.data && typeof error.response.data === "object") {
        const { statusCode, message } = error.response.data;
        errorMessage = message || "An error occurred.";
      }
      // Handle case when error.response.data is a string (malformed or custom error)
      else if (typeof error.response.data === "string") {
        const parsedError = JSON.parse(error.response.data); // Try to parse the string
        statusCode = parsedError.statusCode || error.response.status;
        errorMessage = parsedError.message || "An error occurred.";
      } else {
        errorMsg = "An unexpected error occurred.";
      }
    } catch (e) {
      // In case of a parsing error, fallback to a default error message
      errorMsg = "Error parsing server response.";
    }
    if (error.response.status === 401) {
      errorMsg = errorMessage || "Invalid username or password";
    } else if (error.response.status === 403) {
      errorMsg =
        errorMessage || "You do not have permission to access this resource.";
    } else if (error.response.status === 404) {
      errorMsg = errorMessage || "Resource not found.";
    } else if (error.response.status === 500) {
      errorMsg = errorMessage || "Internal server error. Please try again.";
    } else {
      errorMsg = errorMessage || "An unexpected error occurred.";
    }
    if (Array.isArray(errorMsg)) {
      // If it's an array (multiple validation errors), join with a new line for readability
      showError(errorMsg.join("\n"), "yes"); // Call a function to show the error in Ant Design's message component
    } else {
      showError(errorMsg);
    }
  }
};

// Helper function to show Ant Design error message
const showError = (errorMsg, moreThan = "no") => {
  message.error({
    content: (
      <div style={{ whiteSpace: "pre-line" }}>
        {moreThan === "yes" ? (
          <strong>Errors occurred:</strong>
        ) : (
          <strong>An error occurred:</strong>
        )}
        <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
      </div>
    ),
    duration: 5, // duration in seconds
    className: "error-message", // Add any custom styles you need
  });
};

export default APICaller;
